/**
 * Pure view-model builder for the rewards card in the customer profile.
 *
 * Nothing here decides a level. The ladder comes from rewards-api
 * (`/api/rewards/tier-config`, the same `rule_global_tier` every other screen
 * reads) and the customer's level comes from the summary (`tier.current_tier`,
 * the stored level — DS-26). Adding or retiring a tier in the rewards console
 * shows up here with no deploy, and the level shown matches the Flutter app.
 */

export interface TierConfigEntry {
  name: string;
  xp_min: number;
  xp_max: number | null;
}

export interface TierStyle {
  card: string;
  iconBg: string;
  icon: string;
  text: string;
  bar: string;
  accent: string;
}

export interface TierSegment {
  name: string;
  fillPct: number;
  barClass: string;
  isFirst: boolean;
  isLast: boolean;
}

export interface RewardsCardView {
  tierName: string | null;
  style: TierStyle;
  segments: TierSegment[];
  nextTierName: string | null;
  xpRemaining: number;
  isMaxTier: boolean;
}

// Full class strings on purpose: Tailwind scans this file and only keeps
// classes it finds written literally.
const TIER_STYLES: Record<string, TierStyle> = {
  bronce: {
    card: 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 border-orange-200',
    iconBg: 'bg-orange-400/20',
    icon: 'fa-star text-orange-500',
    text: 'text-orange-800',
    bar: 'bg-orange-400',
    accent: 'text-orange-700',
  },
  plata: {
    card: 'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 border-slate-200',
    iconBg: 'bg-slate-400/20',
    icon: 'fa-medal text-slate-500',
    text: 'text-slate-700',
    bar: 'bg-slate-400',
    accent: 'text-slate-600',
  },
  oro: {
    card: 'bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border-yellow-200',
    iconBg: 'bg-yellow-400/20',
    icon: 'fa-crown text-yellow-600',
    text: 'text-yellow-800',
    bar: 'bg-yellow-500',
    accent: 'text-yellow-700',
  },
  titanio: {
    card: 'bg-gradient-to-br from-zinc-50 via-stone-50 to-zinc-100 border-zinc-300',
    iconBg: 'bg-zinc-500/20',
    icon: 'fa-shield-alt text-zinc-600',
    text: 'text-zinc-800',
    bar: 'bg-zinc-500',
    accent: 'text-zinc-700',
  },
  diamante: {
    card: 'bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 border-cyan-200',
    iconBg: 'bg-cyan-400/20',
    icon: 'fa-gem text-cyan-600',
    text: 'text-cyan-800',
    bar: 'bg-cyan-500',
    accent: 'text-cyan-700',
  },
};

// A tier created in the rewards console with an unknown name still renders.
const DEFAULT_TIER_STYLE: TierStyle = {
  card: 'bg-gradient-to-br from-gray-50 via-white to-gray-100 border-gray-200',
  iconBg: 'bg-gray-400/20',
  icon: 'fa-award text-gray-500',
  text: 'text-gray-800',
  bar: 'bg-gray-400',
  accent: 'text-gray-700',
};

const normalizeName = (name: string | null | undefined): string =>
  (name ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

export const tierStyleFor = (name: string | null | undefined): TierStyle =>
  TIER_STYLES[normalizeName(name)] ?? DEFAULT_TIER_STYLE;

/** Progress of `xp` inside each tier band: full below, partial in, empty above. */
const buildSegments = (ladder: TierConfigEntry[], xp: number): TierSegment[] =>
  ladder.map((tier, i) => {
    const next = ladder[i + 1];
    let fillPct: number;
    if (!next) {
      fillPct = xp >= tier.xp_min ? 100 : 0;
    } else {
      const span = next.xp_min - tier.xp_min;
      const ratio = span > 0 ? (xp - tier.xp_min) / span : xp >= next.xp_min ? 1 : 0;
      fillPct = Math.max(0, Math.min(100, ratio * 100));
    }
    return {
      name: tier.name,
      fillPct,
      barClass: tierStyleFor(tier.name).bar,
      isFirst: i === 0,
      isLast: i === ladder.length - 1,
    };
  });

/**
 * @param summary  `data` of `/api/rewards/admin/summary/:personCode`
 * @param ladder   `data.tiers` of `/api/rewards/tier-config`; `null` when it
 *                 could not be read — the card then shows the level without a
 *                 progress bar instead of inventing thresholds.
 */
export const buildRewardsCardView = (
  summary: any,
  ladder: TierConfigEntry[] | null,
): RewardsCardView => {
  const xp = Number(summary?.xp_balance ?? 0);
  // `current_tier` is the stored level; `recommended_tier` is the fallback for
  // rewards-api deploys that predate it (XP-only level, previous contract).
  const tierName: string | null = summary?.tier?.current_tier ?? summary?.tier?.recommended_tier ?? null;

  const sorted = [...(ladder ?? [])].sort((a, b) => a.xp_min - b.xp_min);
  const currentIndex = sorted.findIndex((t) => normalizeName(t.name) === normalizeName(tierName));
  const next = currentIndex >= 0 ? sorted[currentIndex + 1] ?? null : null;

  return {
    tierName,
    style: tierStyleFor(tierName),
    segments: buildSegments(sorted, xp),
    nextTierName: next?.name ?? null,
    xpRemaining: next ? Math.max(0, next.xp_min - xp) : 0,
    isMaxTier: currentIndex >= 0 && currentIndex === sorted.length - 1,
  };
};
